/**
 * 사내결재 문서함 — [휴일근무일지] 탭 (v2.97.0 · 2026-08-26 · 사용자 요청)
 *
 *   상단: [+ 휴일근무일지 작성]
 *   ① 진행 중인 내 문서 — 작성중·반려·결재 진행 중 (본인 것만)
 *   ② 결재완료 목록
 *
 * 가시성은 서버가 행 단위로 거른다:
 *   작성자 본인 + 결재선 당사자(부서장은 자기 부서만·경영지원·대표이사) + admin
 *   + ⭐**함께 이름이 오른 근무자** (사용자 요청)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as api from '../../lib/expense-api';
import { EXPENSE_STATUS_BADGE } from '../../components/ExpenseDocModal';
import { HolidayWorkModal } from '../../components/HolidayWorkModal';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { useAuth } from '../../lib/auth-context';
import { LoadingLines } from '../../components/ui/Skeleton';

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

export function HolidayWorkTab() {
  const { user } = useAuth();
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<{ id: number | null } | null>(null);

  // 결재완료 목록
  const finalQuery = useQuery({
    queryKey: ['expense', 'docs', 'holiday', 'final', year, q],
    queryFn: () => api.listExpenseDocs({ group: 'holiday', final: true, year, q: q.trim() || undefined }),
    staleTime: 15_000,
  });

  // 진행 중 — 내 것만 추린다(서버는 결재자에게 남의 진행 건도 주지만, 그건 [내 결재함] 자리다)
  const openQuery = useQuery({
    queryKey: ['expense', 'docs', 'holiday', 'open'],
    queryFn: () => api.listExpenseDocs({ group: 'holiday', notFinal: true }),
    staleTime: 15_000,
  });
  // ⭐「내 것」 = 내가 쓴 것 + **내가 근무자로 올라 있는 것** (v2.102.1).
  //   작성자만 보면 함께 근무한 사람에게는 목록이 비어 보인다 — 서버는 주고 있는데
  //   화면이 도로 걸러낸 것이었다(v2.97.0 이 약속한 「근무자도 같이 본다」가 여기서 깨졌다).
  const mine = (openQuery.data?.rows ?? []).filter(
    (r) => r.authorId === user?.userId || r.iAmWorker,
  );

  const rows = finalQuery.data?.rows ?? [];

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">휴일근무일지</div>
        <ul className="list-disc pl-5 space-y-0.5 text-[13px] text-blue-800">
          <li>휴일에 근무한 내용을 적어 결재를 올립니다. 결재선은 <b>지출결재 문서와 같습니다.</b></li>
          <li>여러 명이 함께 근무했으면 <b>대표 한 분이 작성</b>하고 근무자에 나머지를 넣어 주세요.</li>
          <li>근무자로 들어간 분들도 그 문서를 <b>같이 봅니다.</b> 수정·제출·삭제는 <b>작성한 사람만</b> 합니다.</li>
          <li>진행 중인 결재는 <Link to="/approvals" className="underline">내 결재함</Link>에서 처리합니다.</li>
        </ul>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setOpen({ id: null })}
          className="btn btn-primary"
        >
          + 휴일근무일지 작성
        </button>
      </div>

      {/* ① 진행 중인 내 문서 */}
      {mine.length > 0 && (
        <section>
          <h3 className="ui-subsection-title mb-2">
            진행 중인 내 휴일근무일지 <span className="text-slate-400 font-normal">{mine.length}건</span>
          </h3>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-[120px]">문서번호</th>
                  <th className="text-left px-3 py-2 font-medium w-[104px]">상태</th>
                  <th className="text-left px-3 py-2 font-medium w-[110px]">작성일</th>
                  <th className="text-left px-3 py-2 font-medium">근무사유</th>
                </tr>
              </thead>
              <tbody>
                {mine.map((r) => (
                  <tr key={r.id} onClick={() => setOpen({ id: r.id })}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${EXPENSE_STATUS_BADGE[r.status] ?? ''}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.docDate}</td>
                    <td className="px-3 py-2">
                      {/* 남이 쓴 문서(내가 근무자로 들어간 것)는 누가 썼는지 밝힌다 */}
                      <div className="flex items-center gap-1.5 min-w-0">
                        {r.authorId !== user?.userId && (
                          <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {r.authorName} 작성
                          </span>
                        )}
                        <span className="truncate">{r.purpose ?? ''}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ② 결재완료 */}
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="ui-subsection-title">
            결재완료 <span className="text-slate-400 font-normal">{rows.length}건</span>
          </h3>
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="ui-input">
              {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="문서번호·작성자·근무사유 검색…"
              className="ui-input w-60" />
          </div>
        </div>

        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-[120px]">문서번호</th>
                <th className="text-left px-3 py-2 font-medium w-[110px]">작성일</th>
                <th className="text-left px-3 py-2 font-medium w-[92px]">작성자</th>
                <th className="text-left px-3 py-2 font-medium w-[110px]">부서</th>
                <th className="text-left px-3 py-2 font-medium">근무사유</th>
                <th className="px-3 py-2 font-medium w-[52px]"></th>
              </tr>
            </thead>
            <tbody>
              {finalQuery.isLoading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400"><LoadingLines /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                  결재완료된 휴일근무일지가 없습니다.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => setOpen({ id: r.id })}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 font-mono text-xs">{r.docNo}</td>
                  <td className="px-3 py-2 text-slate-600">{r.docDate}</td>
                  <td className="px-3 py-2">{r.authorName}</td>
                  <td className="px-3 py-2 text-slate-600">{r.authorDept ?? ''}</td>
                  <td className="px-3 py-2 truncate">{r.purpose ?? ''}</td>
                  <td className="px-3 py-2 text-center">
                    {r.attachmentId && (
                      <button type="button" title="문서 보기"
                        onClick={(e) => {
                          e.stopPropagation();
                          openServerPdfPreview({
                            pdfUrl: api.attachmentDownloadUrl(r.attachmentId!),
                            filename: `${r.docNo}_휴일근무일지.pdf`,
                          });
                        }}
                        className="text-slate-400 hover:text-blue-600">📄</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {open && <HolidayWorkModal docId={open.id} onClose={() => setOpen(null)} />}
    </div>
  );
}
